import React from "react";
import { cn } from "@strivelabs/ui-core";
import { Favicon, hostOf, looksLikeDomain } from "./citation-favicon";

type AnchorProps = React.ComponentPropsWithoutRef<"a"> & {
  // react-markdown / Streamdown pass the mdast node; keep it off the DOM.
  node?: unknown;
};

/** Flatten a React children tree to its plain-text content. */
function childrenToText(children: React.ReactNode): string {
  return React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") {
        return String(child);
      }
      if (React.isValidElement(child)) {
        return childrenToText((child as React.ReactElement<{ children?: React.ReactNode }>).props.children);
      }
      return "";
    })
    .join("");
}

/**
 * Markdown anchor renderer for chat responses. When a link is a model-emitted
 * citation — an `http(s)` link whose visible text is a bare domain — it renders
 * as a compact favicon chip (ChatGPT/Claude style). Every other link falls back
 * to the standard inline link styling.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CitationLink({ node, href, children, className, ...props }: AnchorProps) {
  const text = childrenToText(children).trim();
  const isCitation = Boolean(href)
    && /^https?:\/\//i.test(href ?? "")
    && looksLikeDomain(text);

  if (!isCitation) {
    return (
      <a
        {...props}
        href={href}
        className={cn(
          "text-custom-text-primary underline hover:text-custom-text-secondary",
          className
        )}
      >
        {children}
      </a>
    );
  }

  const host = hostOf(href as string);

  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noreferrer"
      title={host}
      className={cn(
        "not-prose mx-0.5 inline-flex max-w-[16rem] translate-y-[1px] items-center gap-1 align-middle",
        "rounded-full border border-border bg-muted/40 px-1.5 py-0.5",
        "text-[11px] font-medium leading-none text-custom-text-secondary no-underline",
        "transition-colors hover:bg-accent hover:text-custom-text-primary",
        className
      )}
    >
      <span className="flex size-3 shrink-0 items-center justify-center overflow-hidden rounded-full">
        <Favicon url={href as string} className="size-3" />
      </span>
      <span className="truncate">{host}</span>
    </a>
  );
}
